/**
 * Customer Billing Hook
 * 
 * React Query hook for customer billing data management
 * Handles caching, loading states, and period management
 */

import { useQuery, UseQueryResult, QueryKey } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { 
  calculateCustomerBilling, 
  BillingComparisonData, 
  validateBillingCalculationInputs 
} from '../utils/billingCalculations';
import { 
  getDefaultPeriodType, 
  getAvailablePeriodTypes, 
  calculatePeriodBounds,
  PeriodData 
} from '../utils/periodCalculations';
import { ForecastOptions } from '../utils/forecastingUtils';

export interface UseBillingOptions {
  enabled?: boolean;
  forecastOptions?: ForecastOptions;
  includeComparison?: boolean;
  usePreview?: boolean;
  refetchInterval?: number; // Auto-refresh interval in ms
}

export interface UseBillingResult {
  // Data
  data: BillingComparisonData | undefined;
  currentPeriod: PeriodData;
  
  // Loading states
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
  
  // Period management
  selectedPeriodType: string;
  availablePeriodTypes: Array<{ value: string; label: string }>;
  setPeriodType: (periodType: string) => void;
  
  // Actions
  refetch: () => void;
  
  // Validation
  validationErrors: string[];
}

/**
 * Main hook for customer billing data
 */
export function useCustomerBilling(
  customerId: string | undefined,
  options: UseBillingOptions = {}
): UseBillingResult {
  
  const {
    enabled = true,
    forecastOptions = {},
    includeComparison = true,
    usePreview = false,
    refetchInterval = 5 * 60 * 1000 // 5 minutes default
  } = options;
  
  // Period management state
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>(getDefaultPeriodType());
  
  // Memoized period calculations
  const currentPeriod = useMemo(() => {
    try {
      return calculatePeriodBounds(selectedPeriodType);
    } catch (error) {
      console.error('Error calculating period bounds:', error);
      return calculatePeriodBounds(getDefaultPeriodType());
    }
  }, [selectedPeriodType]);
  
  const availablePeriodTypes = useMemo(() => getAvailablePeriodTypes(), []);
  
  // Input validation
  const validationErrors = useMemo(() => {
    if (!customerId) return ['Customer ID is required'];
    
    const validation = validateBillingCalculationInputs(customerId, selectedPeriodType);
    return validation.errors;
  }, [customerId, selectedPeriodType]);
  
  // Query key for React Query caching
  const queryKey: QueryKey = [
    'customer-billing',
    customerId,
    selectedPeriodType,
    forecastOptions,
    includeComparison,
    usePreview
  ];
  
  // Main React Query
  const query = useQuery<BillingComparisonData, Error>({
    queryKey,
    queryFn: async () => {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }
      
      return calculateCustomerBilling(customerId, selectedPeriodType, {
        forecastOptions,
        includeComparison,
        usePreview
      });
    },
    enabled: enabled && !!customerId && validationErrors.length === 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime in v4)
    refetchInterval: refetchInterval,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Don't retry validation errors
      if (error.message.includes('required') || error.message.includes('invalid')) {
        return false;
      }
      // Retry network errors up to 3 times
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
  
  return {
    // Data
    data: query.data,
    currentPeriod,
    
    // Loading states
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    
    // Period management
    selectedPeriodType,
    availablePeriodTypes,
    setPeriodType: setSelectedPeriodType,
    
    // Actions
    refetch: query.refetch,
    
    // Validation
    validationErrors
  };
}

/**
 * Hook for quick billing preview (limited data for fast loading)
 */
export function useCustomerBillingPreview(
  customerId: string | undefined,
  periodType?: string
): Pick<UseBillingResult, 'data' | 'isLoading' | 'isError' | 'error'> {
  
  return useCustomerBilling(customerId, {
    usePreview: true,
    includeComparison: false,
    refetchInterval: 0 // No auto-refresh for previews
  });
}

/**
 * Hook for period comparison only (current vs previous)
 */
export function useCustomerBillingComparison(
  customerId: string | undefined,
  periodType: string = getDefaultPeriodType()
): UseBillingResult {
  
  const [selectedPeriodType] = useState(periodType);
  
  return useCustomerBilling(customerId, {
    includeComparison: true,
    usePreview: false,
    refetchInterval: 10 * 60 * 1000 // 10 minutes for comparison data
  });
}

/**
 * Helper hook for billing summary statistics across multiple customers
 */
export function useBillingSummary(
  customerIds: string[],
  periodType: string = getDefaultPeriodType(),
  enabled: boolean = true
) {
  
  const queryKey: QueryKey = ['billing-summary', customerIds, periodType];
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      const { calculateBillingSummary } = await import('../utils/billingCalculations');
      return calculateBillingSummary(customerIds, periodType);
    },
    enabled: enabled && customerIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false
  });
}

/**
 * Hook for invalidating billing cache when new calls are made
 */
export function useBillingCacheInvalidation() {
  // This would be used to invalidate cache when new calls are detected
  // Implementation depends on real-time call detection mechanism
  
  // For now, return a manual invalidation function
  return {
    invalidateCustomerBilling: (customerId: string) => {
      // TODO: Implement cache invalidation
      console.log(`Cache invalidation requested for customer: ${customerId}`);
    }
  };
}

/**
 * Helper function to get query key for external cache management
 */
export function getBillingQueryKey(
  customerId: string,
  periodType: string,
  options: UseBillingOptions = {}
): QueryKey {
  const {
    forecastOptions = {},
    includeComparison = true,
    usePreview = false
  } = options;
  
  return [
    'customer-billing',
    customerId,
    periodType,
    forecastOptions,
    includeComparison,
    usePreview
  ];
}
